import { createContext, useContext, useState, useEffect, useCallback, useRef, type ReactNode } from 'react';
import type { ProjectVersionOverview } from '@/types/project';
import { threatcatService } from '@/services/threatcatService';
import { ThreatCatAuthError } from '@/lib/errors';
import { toast } from 'sonner';

const BACKEND_BASE = import.meta.env.VITE_BACKEND_URL || 'http://localhost:8000';

async function fetchFavorites(): Promise<Set<number>> {
  try {
    const res = await fetch(`${BACKEND_BASE}/api/favorites`, { credentials: 'include' });
    if (!res.ok) return new Set();
    const data = await res.json();
    return new Set(data.favorites as number[]);
  } catch {
    return new Set();
  }
}

const STORAGE_KEY = 'tc_cookie';

function loadSavedCookie(): string | null {
  try {
    return sessionStorage.getItem(STORAGE_KEY);
  } catch {
    return null;
  }
}

function persistCookie(cookie: string): void {
  try {
    sessionStorage.setItem(STORAGE_KEY, cookie);
  } catch { /* ignore */ }
}

function removeCookie(): void {
  try {
    sessionStorage.removeItem(STORAGE_KEY);
  } catch { /* ignore */ }
}

interface ThreatCatContextType {
  // Canonical connection state
  cookie: string | null;
  connect: (cookie: string, projects: ProjectVersionOverview[]) => Promise<void>;
  isConnected: boolean;
  projects: ProjectVersionOverview[];
  disconnect: () => void;
  // Reads the cookie synchronously, bypassing React's render cycle. Callers
  // that run right after `connect()`/`disconnect()` (e.g. a modal's
  // `onConnected` callback) would otherwise see a stale `cookie` closed over
  // from the render before the connection state settled.
  getCookie: () => string | null;
}

const ThreatCatContext = createContext<ThreatCatContextType | undefined>(undefined);

export function ThreatCatProvider({ children }: { children: ReactNode }) {
  const [cookie, setCookie] = useState<string | null>(loadSavedCookie);
  const [isConnected, setIsConnected] = useState(false);
  const [projects, setProjects] = useState<ProjectVersionOverview[]>([]);
  const cookieRef = useRef(cookie);
  const getCookie = useCallback(() => cookieRef.current, []);

  // Auto-reconnect on mount when a saved cookie exists.
  useEffect(() => {
    if (!cookie || isConnected) return;

    let cancelled = false;
    Promise.all([
      threatcatService.fetchThreatCatProjects(cookie),
      fetchFavorites(),
    ])
      .then(([fetchedProjects, favSet]) => {
        if (cancelled || cookieRef.current !== cookie) return;
        const safeProjects = Array.isArray(fetchedProjects) ? fetchedProjects : [];
        const enriched = safeProjects.map(p => ({
          ...p,
          is_favorite: favSet.has(p.project_id),
        }));
        setProjects(enriched);
        setIsConnected(true);
      })
      .catch((error: unknown) => {
        if (cancelled || cookieRef.current !== cookie) return;
        if (error instanceof ThreatCatAuthError) {
          removeCookie();
          cookieRef.current = null;
          setCookie(null);
          setIsConnected(false);
          setProjects([]);
        } else {
          toast.error(error instanceof Error ? error.message : 'Could not reconnect to ThreatCat.');
        }
      });

    return () => { cancelled = true; };
  }, []);

  const connect = useCallback(async (nextCookie: string, fetchedProjects: ProjectVersionOverview[]) => {
    persistCookie(nextCookie);
    cookieRef.current = nextCookie;
    setCookie(nextCookie);
    const favSet = await fetchFavorites();
    const safeProjects = Array.isArray(fetchedProjects) ? fetchedProjects : [];
    const enriched = safeProjects.map(p => ({ ...p, is_favorite: favSet.has(p.project_id) }));
    setProjects(enriched);
    setIsConnected(true);
  }, []);

  const disconnect = useCallback(() => {
    removeCookie();
    cookieRef.current = null;
    setCookie(null);
    setIsConnected(false);
    setProjects([]);
  }, []);

  return (
    <ThreatCatContext.Provider
      value={{
        cookie,
        connect,
        isConnected,
        projects,
        disconnect,
        getCookie,
      }}
    >
      {children}
    </ThreatCatContext.Provider>
  );
}

export function useThreatCat() {
  const context = useContext(ThreatCatContext);
  if (!context) {
    throw new Error('useThreatCat must be used within ThreatCatProvider');
  }
  return context;
}
